import { useState } from 'react';
import { CasStackRow } from 'src/compass/shared/components';
import { getDateInMoment } from 'src/compass/shared/util/casMoment';

import { ClaimDetailsProps } from './ClaimDetails';
import ClaimTrackingStatusButton from './ClaimTrackingStatusButton';
import EnterActionReasonDialog from './EnterActionReasonDialog';
import NoticeReceivedDialog from './NoticeReceivedDialog';
import ClaimCallbackDialog from './ClaimCallbackDialog';

export type StatusButtonConfig = {
  label: string;
  url: string;
  icon?: string;
  showAsLink?: boolean;
  color?: 'primary' | 'secondary' | 'error';
  dialog?: React.ComponentType<any>;
  dialogProps?: Function | Record<string, any>; // Can be function or object
  payload?: Record<string, boolean>;
};

type StatusType =
  | 'null'
  | 'Send'
  | 'Docusign In Process'
  | 'Docusign Review'
  | 'Attached'
  | 'Approved'
  | 'Callback';

const BACK_BUTTON: StatusButtonConfig = {
  label: 'Back',
  url: '/Sce/Claims/Back',
  showAsLink: true,
  color: 'secondary',
  dialog: EnterActionReasonDialog,
  dialogProps: (data: any) => ({ title: `Clear ${data.status}` }),
};

const CLOSE_CLAIM_BUTTON: StatusButtonConfig = {
  label: 'Close Claim',
  url: '/Sce/Claims/Close',
  icon: 'mdi:close-thick',
  showAsLink: true,
  color: 'secondary',
  dialog: EnterActionReasonDialog,
  dialogProps: { title: 'Close Claim' },
};

const STATUS_BUTTON_CONFIG: Record<StatusType, StatusButtonConfig[]> = {
  null: [
    {
      label: 'Notice Received',
      url: '/Sce/Claims/NoticeReceived',
      icon: 'tabler:message-down',
      dialog: NoticeReceivedDialog,
    },
  ],
  Send: [
    { label: 'Notice Sent', url: '/Sce/Claims/Sent', icon: 'material-symbols:mail-outline' },
    BACK_BUTTON,
    CLOSE_CLAIM_BUTTON,
  ],
  'Docusign In Process': [
    {
      label: 'Docusign Received',
      url: '/Sce/Claims/DocusignReceived',
      icon: 'uil:file-download-alt',
    },
    BACK_BUTTON,
    CLOSE_CLAIM_BUTTON,
  ],
  'Docusign Review': [
    { label: 'Attached', url: '/Sce/Claims/Attached', icon: 'material-symbols:attach-file' },
    {
      label: 'Deficient',
      url: '/Sce/Claims/CallbackCreate',
      icon: 'mdi:close-octagon',
      color: 'error',
      dialog: ClaimCallbackDialog,
      dialogProps: {
        title: 'Deficient: Assign Callback',
        defaultValues: { type: 'Deficient' },
      },
    },
    BACK_BUTTON,
    CLOSE_CLAIM_BUTTON,
  ],
  Attached: [
    {
      label: 'Approved',
      url: '/Sce/Claims/Reviewed',
      payload: { approved: true },
      icon: 'material-symbols:check',
    },
    {
      label: 'Approved & Verified',
      url: '/Sce/Claims/Reviewed',
      payload: { approved: true, verified: true },
      icon: 'material-symbols:verified',
    },
    {
      label: 'Rejected',
      url: '/Sce/Claims/CallbackCreate',
      icon: 'mdi:close-octagon',
      color: 'error',
      dialog: ClaimCallbackDialog,
      dialogProps: {
        title: 'Rejected: Assign Callback',
        defaultValues: { type: 'Rejected' },
      },
    },
    BACK_BUTTON,
  ],
  Approved: [
    {
      label: 'Verified',
      url: '/Sce/Claims/Reviewed',
      payload: { verified: true },
      icon: 'material-symbols:verified',
    },
    BACK_BUTTON,
  ],
  Callback: [
    {
      label: 'Callback complete (Resend ready)',
      url: '/Sce/Claims/CallbackComplete',
      icon: 'tabler:phone-done',
      dialog: ClaimCallbackDialog,
      dialogProps: (data: any) => ({
        title: 'Callback Complete',
        submitButtonLabel: 'Done',
        mutationType: 'update',
        defaultValues: {
          id: data?.callback?.id,
          type: data?.callback?.type,
          callbackNotes: data?.callback?.callbackNotes,
          assignedToEmployeeId: data?.callback?.assignedToEmployeeId,
        },
      }),
    },
    BACK_BUTTON,
    CLOSE_CLAIM_BUTTON,
  ],
};

export default function ClaimTrackingStatusActions({ data, onUpdated }: ClaimDetailsProps) {
  const { status, id: endorsementId } = data || {};

  const [activeDialogButton, setActiveDialogButton] = useState<StatusButtonConfig | null>(null);

  const buttons = STATUS_BUTTON_CONFIG?.[status as StatusType] || [];

  // Include all props that are needed by any dialog
  const defaultDialogProps = {
    onUpdated,
    endorsementId,
    status,
    onClose: () => setActiveDialogButton(null),
  };

  if (status === 'Done') return null;

  if (getDateInMoment(data.endDate).isAfter(new Date())) return null;

  const DialogComponent = activeDialogButton?.dialog;

  const dialogProps = () => {
    if (typeof activeDialogButton?.dialogProps === 'function')
      return activeDialogButton?.dialogProps(data);

    return activeDialogButton?.dialogProps;
  }

  return (
    <>
      <CasStackRow gapSize="large">
        {buttons.map((button) => {
          const adjButton = {... button};
          if (adjButton.label==="Notice Sent")  // TODO - brittle
              adjButton.label = `${data.whatSent  } Sent`;
          return <ClaimTrackingStatusButton
            key={button.label}
            button={adjButton}
            endorsementId={endorsementId}
            onUpdated={onUpdated}
            onDialogChange={setActiveDialogButton}
          />
        })}
      </CasStackRow>
      {DialogComponent && (
        <DialogComponent {...activeDialogButton} {...defaultDialogProps} {...dialogProps()} />
      )}
    </>
  );
}
